import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { qaResponsibilityApi } from '../lib/qa-responsibility-api.js';
import type { QaResponsibilityConfirmInput } from '../lib/qa-responsibility-api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * 現在のi18nロケールから 'ja' | 'en' を返す
 * @graph-connects none
 */
function useApiLocale(): 'ja' | 'en' {
  const { i18n } = useTranslation();
  const lang = i18n.language ?? 'ja';
  return lang.startsWith('ja') ? 'ja' : 'en';
}

/**
 * QA責任評価一覧を取得
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA責任評価一覧をTanStack Query経由で取得
 * @graph-connects /qa-responsibilities [calls] QA責任評価一覧取得API
 */
export function useQaResponsibilities(params?: {
  startDate?: string;
  endDate?: string;
  qaReviewStatus?: string;
  team?: string;
}) {
  const locale = useApiLocale();
  return useQuery({
    queryKey: [...queryKeys.qaResponsibility.list(params), locale] as const,
    queryFn: () => qaResponsibilityApi.getList({ ...params, locale }),
  });
}

/**
 * QA責任評価詳細を取得
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA責任評価詳細をTanStack Query経由で取得（locale指定で翻訳対応）
 * @graph-connects /qa-responsibilities/:issueKey [calls] QA責任評価詳細取得API
 */
export function useQaResponsibilityDetail(issueKey: string, locale?: 'ja' | 'en') {
  const autoLocale = useApiLocale();
  const effectiveLocale = locale ?? autoLocale;
  return useQuery({
    queryKey: [...queryKeys.qaResponsibility.detail(issueKey), effectiveLocale] as const,
    queryFn: () => qaResponsibilityApi.getDetail(issueKey, effectiveLocale),
    enabled: issueKey.length > 0,
  });
}

/**
 * QA責任を確定する
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA責任をFirestore経由で確定（Backlog書き戻し→EventArcでBQ同期）
 * @graph-connects /qa-responsibilities/:issueKey [calls] QA責任確定API
 */
export function useConfirmQaResponsibility(issueKey: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: QaResponsibilityConfirmInput) => qaResponsibilityApi.confirm(issueKey, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.qaResponsibility.detail(issueKey),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.qaResponsibility.all,
      });
    },
  });
}
